import { useState } from "react";
import { ApiQuery, SortDirections } from "~/modules/filters/types";
import { ManagerColumns, ManagerWithAggregates } from "../..";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";

// // // //

/**
 * MANAGER_SORT_MAP
 * Map each ManagerColumns to a key used to in-memory sort the array of items using `sortItemsByProperty`
 */
const MANAGER_SORT_MAP: Record<ManagerColumns, string> = {
    [ManagerColumns.name]: "name",
    [ManagerColumns.assetCount]: "rwa_asset_count",
    [ManagerColumns.bridgedTokenValueDollar]: "rwa_value.val",
    [ManagerColumns.holdingAddressesCount]: "rwa_holders.val",
    [ManagerColumns.trailing30DayTransferVolume]:
        "trailing_30_day_transfer_volume.val",
    [ManagerColumns.trailing30DayActiveAddressesCount]:
        "trailing_30_day_active_addresses_count.val",
    [ManagerColumns.id]: "id",
    [ManagerColumns.slug]: "slug",
    [ManagerColumns.description]: "description",
    [ManagerColumns.colorHex]: "color_hex",
    [ManagerColumns.iconUrl]: "icon_url",
    [ManagerColumns.distributedValue]: "distributed_value.val",
    [ManagerColumns.representedValue]: "represented_value.val",
};

// // // //

/**
 * useInMemoryManagerSort
 * Hook for sorting managers in memory
 */
export function useInMemoryManagerSort(props: {
    rows: ManagerWithAggregates[];
    sort: { field: ManagerColumns; direction: SortDirections };
}) {
    const { rows, sort } = props;
    const [query, setQuery] = useState<ApiQuery>({
        sort: {
            field: sort.field,
            direction: sort.direction,
        },
        pagination: {
            page: 1,
            perPage: 100,
        },
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: MANAGER_SORT_MAP[query.sort?.field as ManagerColumns],
        direction: query.sort?.direction || SortDirections.asc,
        nullsLast: true,
    });

    return {
        query,
        setQuery,
        sortedItems,
    };
}
